import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnActiveCallsCount } from 'flow/selectors/call-selectors';
import {
    selectActiveMeetingReconnectActive,
    selectIsSameLobbyAsActiveMeeting,
    selectLobbyHostGateClosed,
    selectLobbyMeetingCode,
    selectLobbyMeetingJoinBlockedReason,
    selectLobbyViewerIsMeetingHost,
    selectMeetingIsActive,
    selectMicAllowed,
} from 'flow/selectors/meeting-selectors';
import { MeetingJoinBlockedReason } from 'flow/utils/meeting-constants';
import { getIfValidBrowser } from 'flow/utils/meeting-utils';

/**
 * Why this person cannot join yet, or an empty string when nothing is stopping
 * them. Drives both the lobby's disabled join button and the tooltip explaining
 * it.
 *
 * Ordered: the first thing that stops you is the thing you are told about, so a
 * meeting that has not loaded says so rather than complaining about your name.
 *
 * Derived rather than stored. It is a pure function of the store, the name and
 * the language, so each input is subscribed to directly and the answer is right
 * on the first render — where holding it in state left the button briefly
 * enabled with no reason, and left the reason a render behind thereafter.
 */
const useJoinMeetingErrorText = (name: string): string => {
    const { i18next } = useAppContext();

    const meetingCode = useAppSelector(selectLobbyMeetingCode);
    const viewerIsMeetingHost = useAppSelector(selectLobbyViewerIsMeetingHost);
    const meetingJoinBlockedReason = useAppSelector(
        selectLobbyMeetingJoinBlockedReason,
    );
    const hostGateClosed = useAppSelector(selectLobbyHostGateClosed);
    const meetingIsActive = useAppSelector(selectMeetingIsActive);
    const isSameMeeting = useAppSelector(selectIsSameLobbyAsActiveMeeting);
    const micAllowed = useAppSelector(selectMicAllowed);
    const reconnectActive = useAppSelector(selectActiveMeetingReconnectActive);
    const activeCallsCount = useAppSelector(selectOwnActiveCallsCount);

    if (!meetingCode) {
        return i18next.t('videoconference.error.reload');
    }
    if (!getIfValidBrowser()) {
        return i18next.t('videoconference.lobby.support_browser', {
            browser1: 'Firefox',
            browser2: 'Chrome',
        });
    }
    if (
        !viewerIsMeetingHost &&
        meetingJoinBlockedReason &&
        meetingJoinBlockedReason !== MeetingJoinBlockedReason.NONE.key
    ) {
        return i18next.t('videoconference.lobby.no_access');
    }
    // VID-198: the meeting waits for its host and none has arrived. A guest is
    // held too: asking to be let in (VID-76) needs someone inside who can
    // answer, and everyone who could is waiting at this same gate.
    if (hostGateClosed) {
        return i18next.t('videoconference.lobby.waiting_for_host');
    }
    if (meetingIsActive && !isSameMeeting) {
        return i18next.t('videoconference.lobby.already_active');
    }
    if (!micAllowed) {
        return i18next.t('videoconference.lobby.mic_access');
    }
    if (name === '') {
        return i18next.t('videoconference.lobby.enter_name');
    }
    if (reconnectActive) {
        return i18next.t('videoconference.error.network_reconnect');
    }
    if (activeCallsCount > 0) {
        return i18next.t('videoconference.lobby.call_active');
    }

    return '';
};

export default useJoinMeetingErrorText;
